/**
 * AP-27 — **Regiebericht** (Baustein `SITE_DOCS.REGIE`): was Gerät, ERP und Server über die Art
 * gleich wissen müssen — Zeilen, Zuschläge, Pflichtangaben und vor allem der **kanonische
 * Fachteil** (`regieFachteil`), den beide Seiten mit derselben Funktion bauen (AP-46 6.).
 *
 * Spezifikation: docs/Arbeitspakete/AP-27/AP-27_Regieberichte.md
 */
import { festeZahl, kanonischerText } from './kanonisch'
import type { SiteDocDiktatZeile } from './site-docs-ki'

/** Fassung des Fachteils. Eine Änderung am Aufbau unten ist eine neue Fassung (AP-46 6.). */
export const REGIE_SCHEMA_VERSION = 1

/** Zeilenart ist `ArticleKind` (RB-5): Stunden, Material, Geräte, Sonstiges. */
export const REGIE_LINE_KINDS = ['SERVICE', 'MATERIAL', 'EQUIPMENT', 'OTHER'] as const
export type RegieLineKind = (typeof REGIE_LINE_KINDS)[number]

export const REGIE_LINE_ORIGINS = ['MANUAL', 'BOOKING', 'DICTATION'] as const
export type RegieLineOrigin = (typeof REGIE_LINE_ORIGINS)[number]

/** Bestätigungstext der Art (AP-27 2.) — je Mandant änderbar. */
export const REGIE_BESTAETIGUNGSTEXT_VORGABE =
  'Ich bestätige, dass die oben angeführten Leistungen am {Datum} auf der Baustelle {Projekt} erbracht wurden.'

/** Ein Eintrag der Zuschlagsliste des Mandanten (RB-18). */
export interface RegieZuschlag {
  key: string
  label: string
  percent: number
  active: boolean
}

/** Vorgabe der Zuschlagsliste — Platzhalter, die jeder Mandant nach seinem Kollektivvertrag setzt (12.3). */
export const REGIE_ZUSCHLAEGE_VORGABE: readonly RegieZuschlag[] = [
  { key: 'OVERTIME', label: 'Überstunde', percent: 50, active: true },
  { key: 'SATURDAY', label: 'Samstag', percent: 50, active: true },
  { key: 'SUNDAY_HOLIDAY', label: 'Sonn- und Feiertag', percent: 100, active: true },
  { key: 'NIGHT', label: 'Nacht', percent: 25, active: true },
  { key: 'HARDSHIP', label: 'Erschwernis', percent: 20, active: true },
]

/** Einstellungen der Art in `SiteDocumentSetting.extra`. */
export interface RegieExtra {
  zeilenarten: RegieLineKind[]
  zuschlaege: RegieZuschlag[]
}

export const REGIE_EXTRA_VORGABE: RegieExtra = {
  zeilenarten: [...REGIE_LINE_KINDS],
  zuschlaege: REGIE_ZUSCHLAEGE_VORGABE.map((z) => ({ ...z })),
}

export function regieExtra(extra: unknown): RegieExtra {
  const e = (extra ?? {}) as Partial<RegieExtra>
  const arten = Array.isArray(e.zeilenarten) ? e.zeilenarten.filter((k): k is RegieLineKind => (REGIE_LINE_KINDS as readonly string[]).includes(k)) : []
  const zuschlaege = Array.isArray(e.zuschlaege)
    ? e.zuschlaege
        .filter((z): z is RegieZuschlag => !!z && typeof z.key === 'string' && typeof z.label === 'string')
        .map((z) => ({ key: z.key, label: z.label, percent: Number(z.percent) || 0, active: z.active !== false }))
    : REGIE_EXTRA_VORGABE.zuschlaege
  return { zeilenarten: arten.length ? arten : [...REGIE_LINE_KINDS], zuschlaege }
}

/**
 * RN-2 (AP-47 15.) — **Prüft das `extra` des Regieberichts** beim Speichern der Einstellungen:
 * Zeilenarten aus der Liste (mindestens eine), Zuschläge mit eindeutigem Schlüssel, Bezeichnung und
 * Prozentsatz 0–1000, höchstens 30. Liefert die Fehlerliste — leer heißt gültig.
 */
export function regiePruefeExtra(extra: unknown): string[] {
  const f: string[] = []
  if (extra == null) return f
  if (typeof extra !== 'object' || Array.isArray(extra)) return ['extra']
  const e = extra as Record<string, unknown>
  for (const k of Object.keys(e)) if (k !== 'zeilenarten' && k !== 'zuschlaege') f.push(`extra.${k}`)
  if (e.zeilenarten !== undefined) {
    if (!Array.isArray(e.zeilenarten) || !e.zeilenarten.length || e.zeilenarten.some((k) => !(REGIE_LINE_KINDS as readonly unknown[]).includes(k))) f.push('zeilenarten')
  }
  if (e.zuschlaege !== undefined) {
    if (!Array.isArray(e.zuschlaege)) return [...f, 'zuschlaege']
    if (e.zuschlaege.length > 30) f.push('zuschlaege.max')
    const keys = new Set<string>()
    e.zuschlaege.forEach((roh, i) => {
      const z = roh as Record<string, unknown>
      if (!z || typeof z !== 'object') return f.push(`zuschlaege.${i}`)
      if (typeof z.key !== 'string' || !/^[A-Za-z0-9_-]{1,60}$/.test(z.key) || keys.has(z.key)) f.push(`zuschlaege.${i}.key`)
      else keys.add(z.key)
      if (typeof z.label !== 'string' || !z.label.trim() || z.label.length > 100) f.push(`zuschlaege.${i}.label`)
      if (typeof z.percent !== 'number' || !Number.isFinite(z.percent) || z.percent < 0 || z.percent > 1000) f.push(`zuschlaege.${i}.percent`)
      if (z.active !== undefined && typeof z.active !== 'boolean') f.push(`zuschlaege.${i}.active`)
    })
  }
  return f
}

/** „angeordnet von / am" (RB-17) — Inhalt, optional. */
export interface RegieAnordnung {
  von: string
  funktion?: string | null
  am?: string | null
}

/** Eine Zeile, wie Gerät und ERP sie führen — Inhalt und Büroarbeit zusammen. */
export interface RegieZeile {
  clientId: string
  kind: RegieLineKind
  sortOrder: number
  description: string
  /** Menge — Zahl oder Dezimal-Zeichenkette (`"2.5"`). */
  quantity: number | string
  unitCode: string
  personName?: string | null
  qualificationName?: string | null
  surchargeLabel?: string | null
  fromTime?: string | null
  toTime?: string | null
  articleNumber?: string | null
  articleName?: string | null
  // Büroarbeit — nicht im Inhalt (AP-46 BD-24)
  origin?: RegieLineOrigin
  unitId?: string | null
  personUserId?: string | null
  articleId?: string | null
  qualificationId?: string | null
  surchargeKey?: string | null
  /** Hinweise vom Gerät: Buchungen, aus denen die Zeile vorbelegt wurde (4.3). */
  bookingHints?: string[]
  // Nur in Antworten des Servers (ERP): Kennung der Zeile, verknüpfte Buchungen, Abrechnung (7.)
  id?: string
  linkedBookings?: string[]
  billedAt?: string | null
}

/** Der Fachinhalt am Kopf und in den Zeilen, wie er übertragen wird. */
export interface RegieInhalt {
  beschreibung: string
  anordnung?: RegieAnordnung | null
  zeilen: RegieZeile[]
}

const leer = (t: string | null | undefined): string | undefined => {
  const k = t == null ? '' : kanonischerText(t)
  return k ? k : undefined
}

/** Fachliche Reihenfolge: `sortOrder`, bei Gleichstand die Kennung — auf jedem Gerät gleich. */
export function regieSortiert<T extends { sortOrder: number; clientId: string }>(zeilen: readonly T[]): T[] {
  return [...zeilen].sort((a, b) => a.sortOrder - b.sortOrder || (a.clientId < b.clientId ? -1 : a.clientId > b.clientId ? 1 : 0))
}

/**
 * **Der kanonische Fachteil**, `schemaVersion` 1 (AP-27 8.):
 * `{ beschreibung, anordnung?: { von, funktion?, am }, zeilen: [{ art, beschreibung, menge, einheit,
 * person?, qualifikation?, zuschlag?, von?, bis?, artikel?: { nummer, bezeichnung } }] }`.
 *
 * Mengen mit drei Nachkommastellen als Zeichenkette. Zuordnungen (Artikel-, Einheiten-, Personen-ID,
 * Buchungen, Herkunft) stehen bewusst **nicht** darin (AP-46 BD-24); der Prozentsatz eines
 * Zuschlags auch nicht — er ist Preis (RB-18).
 */
export function regieFachteil(inhalt: RegieInhalt): Record<string, unknown> {
  const a = inhalt.anordnung
  const anordnung = a && leer(a.von) ? { von: leer(a.von), funktion: leer(a.funktion ?? null), am: leer(a.am ?? null)?.slice(0, 10) } : undefined
  return {
    beschreibung: kanonischerText(inhalt.beschreibung ?? ''),
    anordnung,
    zeilen: regieSortiert(inhalt.zeilen).map((z) => ({
      art: z.kind,
      beschreibung: kanonischerText(z.description),
      menge: festeZahl(z.quantity, 3),
      einheit: kanonischerText(z.unitCode),
      person: leer(z.personName),
      qualifikation: leer(z.qualificationName),
      zuschlag: leer(z.surchargeLabel),
      von: leer(z.fromTime),
      bis: leer(z.toTime),
      artikel: leer(z.articleName) || leer(z.articleNumber) ? { nummer: leer(z.articleNumber) ?? null, bezeichnung: leer(z.articleName) ?? null } : undefined,
    })),
  }
}

/** Pflichtangaben vor der Unterschrift (AP-27 3.): mindestens eine Zeile, jede mit Beschreibung, Menge > 0, Einheit. */
export function regiePruefe(inhalt: Pick<RegieInhalt, 'zeilen'>): string[] {
  const fehler: string[] = []
  if (!inhalt.zeilen.length) fehler.push('lines')
  for (const z of inhalt.zeilen) {
    if (!kanonischerText(z.description ?? '')) fehler.push(`line:${z.clientId}:description`)
    const menge = Number(z.quantity)
    if (!Number.isFinite(menge) || menge <= 0) fehler.push(`line:${z.clientId}:quantity`)
    if (!kanonischerText(z.unitCode ?? '')) fehler.push(`line:${z.clientId}:unit`)
  }
  return fehler
}

/** `HH:mm` → Minuten; leer oder unlesbar → `null`. */
export function regieMinuten(zeit: string | null | undefined): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec((zeit ?? '').trim())
  if (!m) return null
  const h = Number(m[1])
  const min = Number(m[2])
  return h > 23 || min > 59 ? null : h * 60 + min
}

/** Dauer in Stunden aus Von/Bis — über Mitternacht wird ein Tag addiert; auf 0,01 h gerundet. */
export function regieDauer(von: string | null | undefined, bis: string | null | undefined): number | null {
  const a = regieMinuten(von)
  const b = regieMinuten(bis)
  if (a === null || b === null) return null
  const d = b >= a ? b - a : b + 1440 - a
  return Math.round((d / 60) * 100) / 100
}

/** Stunden des Berichts: gesamt und je Zuschlag (leer = ohne Zuschlag). */
export function regieStunden(zeilen: readonly Pick<RegieZeile, 'kind' | 'quantity' | 'surchargeLabel'>[]): { gesamt: number; jeZuschlag: { zuschlag: string | null; stunden: number }[] } {
  const je = new Map<string | null, number>()
  let gesamt = 0
  for (const z of zeilen) {
    if (z.kind !== 'SERVICE') continue
    const h = Number(z.quantity) || 0
    gesamt += h
    const k = z.surchargeLabel?.trim() || null
    je.set(k, (je.get(k) ?? 0) + h)
  }
  const runde = (n: number) => Math.round(n * 1000) / 1000
  return {
    gesamt: runde(gesamt),
    jeZuschlag: [...je.entries()].sort(([a], [b]) => (a === null ? -1 : b === null ? 1 : a.localeCompare(b))).map(([zuschlag, stunden]) => ({ zuschlag, stunden: runde(stunden) })),
  }
}

/** Stunden für Anzeige und Journal: `6,5` — höchstens drei, mindestens keine Nachkommastelle. */
export function regieStundenText(h: number): string {
  return String(Math.round(h * 1000) / 1000).replace('.', ',')
}

// ── Vorbelegung aus der Zeiterfassung (AP-27 4.) ────────────────────────────

/**
 * Eine Zeitbuchung als Vorschlag für eine Stundenzeile. Keine Uhrzeiten (4.2): Der Polier sieht Name,
 * Dauer, Tätigkeit und Qualifikation. `LINKED` hängt schon an einer Zeile eines aktiven Berichts,
 * `BILLED` ist über den Regievorschlag abgerechnet — beide lassen sich nicht übernehmen (RB-8, RB-20).
 */
export interface RegieVorbelegung {
  timeEntryId: string
  userId: string
  personName: string
  date: string
  minutes: number
  activity: string | null
  qualification: string | null
  status: 'FREE' | 'LINKED' | 'BILLED'
  /** Nummer des Berichts, an dem die Buchung hängt — „Entwurf" ohne Nummer. */
  linkedReport: string | null
}

/** Was das Gerät für den Regiebericht offline braucht (`GET /site-docs/regie/device-cache`, 10.1). */
export interface RegieGeraeteVorrat {
  /** Nur mit `ARTICLES`: Artikel der Stückliste des Projekts und die 200 zuletzt in Regieberichten verwendeten (RB-11). */
  articles: { id: string; number: string; name: string; kind: RegieLineKind; unitId: string | null; unitCode: string }[]
  employees: { id: string; name: string }[]
  units: { id: string | null; code: string; name: string }[]
  /** Aktive Zuschlagsarten des Mandanten. */
  surcharges: RegieZuschlag[]
  /** Die eigenen Buchungen auf diesem Projekt der letzten 14 Tage — die Vorbelegung ohne Netz (4.2). */
  ownBookings: RegieVorbelegung[]
  fetchedAt: string
}

/** Stunden aus Minuten, auf drei Stellen gerundet — `duration` ist netto, keine Pause abziehen. */
export function regieStundenAusMinuten(minuten: number): number {
  return Math.round((minuten / 60) * 1000) / 1000
}

/** Eine Stundenzeile aus einer Buchung — mit der Buchung als Hinweis für die Verknüpfung (4.3). */
export function regieZeileAusBuchung(b: RegieVorbelegung, clientId: string, sortOrder: number, beschreibung = 'Arbeitszeit'): RegieZeile {
  return {
    clientId,
    kind: 'SERVICE',
    sortOrder,
    description: b.activity?.trim() || beschreibung,
    quantity: regieStundenAusMinuten(b.minutes),
    unitCode: 'h',
    personName: b.personName,
    qualificationName: b.qualification,
    surchargeLabel: null,
    fromTime: null,
    toTime: null,
    articleNumber: null,
    articleName: null,
    origin: 'BOOKING',
    unitId: null,
    personUserId: b.userId,
    articleId: null,
    qualificationId: null,
    surchargeKey: null,
    bookingHints: [b.timeEntryId],
  }
}

// ── AP-27 19. — Export der Regiezeilen und Fachinhalt für die Schnittstelle ──

/**
 * Eine Regiezeile, wie sie ein fremdes ERP bekommt — im Export (Excel) und beim Abholen über die
 * Schnittstelle in **derselben** Form. Projekt aus dem Schnappschuss, dazu die Kennung im Fremdsystem,
 * wenn das Projekt aus der Zeiterfassung synchronisiert ist.
 */
export interface RegieExportZeile {
  lineId: string
  reportNumber: string
  documentDate: string
  projectNumber: string | null
  projectName: string | null
  projectExternalId: string | null
  signedBy: string | null
  kind: RegieLineKind
  personName: string | null
  qualificationName: string | null
  description: string
  quantity: number
  unitCode: string
  surchargeLabel: string | null
  articleNumber: string | null
  articleName: string | null
  fromTime: string | null
  toTime: string | null
  billedAt: string | null
}

/** Spalten des Exports, in der Reihenfolge der Spezifikation (19.). */
export const REGIE_EXPORT_SPALTEN: readonly { key: keyof RegieExportZeile; label: string }[] = [
  { key: 'reportNumber', label: 'Bericht' },
  { key: 'documentDate', label: 'Datum' },
  { key: 'projectNumber', label: 'Projektnummer' },
  { key: 'projectName', label: 'Projekt' },
  { key: 'projectExternalId', label: 'Kennung im Fremdsystem' },
  { key: 'signedBy', label: 'Unterzeichner' },
  { key: 'kind', label: 'Art' },
  { key: 'personName', label: 'Person' },
  { key: 'qualificationName', label: 'Qualifikation' },
  { key: 'description', label: 'Leistung' },
  { key: 'quantity', label: 'Menge' },
  { key: 'unitCode', label: 'Einheit' },
  { key: 'surchargeLabel', label: 'Zuschlag' },
  { key: 'articleNumber', label: 'Artikelnummer' },
  { key: 'articleName', label: 'Artikel' },
]

/**
 * AP-27 18. — eine Regiezeile aus einem übernommenen Diktat-Eintrag: hinter den vorhandenen, mit
 * `origin = DICTATION`. Die Verknüpfung zu Buchungen setzt der Vorschlag nicht (4.3).
 */
export function regieZeileAusDiktat(d: SiteDocDiktatZeile, clientId: string, sortOrder: number): RegieZeile {
  return {
    clientId,
    kind: d.kind,
    sortOrder,
    description: d.description,
    quantity: d.quantity,
    unitCode: d.unitCode,
    personName: d.personName,
    personUserId: d.personUserId,
    fromTime: d.fromTime,
    toTime: d.toTime,
    articleId: d.articleId,
    articleNumber: d.articleNumber,
    articleName: d.articleName,
    unitId: d.unitId,
    surchargeKey: d.surchargeKey,
    surchargeLabel: d.surchargeLabel,
    origin: 'DICTATION',
  }
}
